'use client';

import { useState } from 'react';

import Image from 'next/image';

import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  ArrowLeft,
  Mail,
  Phone,
  MessageSquare,
  MessageCircle,
  Share2,
  Smartphone,
  Check,
  Sparkles,
  Calculator,
} from 'lucide-react';

import { EmailGateStep } from '@/components/tools/email-gate-step';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useTranslation } from '@/lib/i18n/context';
import { Link } from '@/lib/i18n/link';

type Step = 1 | 2 | 3 | 4 | 5 | 'email' | 'result';

/** Latin → Arabic-Indic digits, for UI counters only (never calculation output). */
const toArabicDigits = (n: number) =>
  String(n).replace(/[0-9]/g, (d) => '٠١٢٣٤٥٦٧٨٩'[Number(d)]);

const CONTENT = {
  en: {
    thats: "That's",
    anotherHelpdesk: 'another helpdesk',
    currentlyUsing: "You're currently using",
    switchToOpen: 'Switch to Open and eliminate seat-based pricing entirely —',
    noHiddenCosts: 'No hidden costs.',
    channelRates: 'Channel rates:',
    avgChannelRate: 'Average channel rate',
    multiplier: 'multiplier',
    interactionTypes: {
      inbound: 'Inbound',
      outbound: 'Outbound',
      both: 'Both',
    } as Record<string, string>,
  },
  ar: {
    thats: 'يعني',
    anotherHelpdesk: 'نظام دعم آخر',
    currentlyUsing: 'أنت تستخدم حالياً',
    switchToOpen: 'انتقل إلى Open وتخلّص من التسعير بالمقاعد نهائياً —',
    noHiddenCosts: 'ولا تكاليف مخفية.',
    channelRates: 'معدلات القنوات:',
    avgChannelRate: 'متوسط معدل القنوات',
    multiplier: 'مضاعف',
    interactionTypes: {
      inbound: 'وارد',
      outbound: 'صادر',
      both: 'كلاهما',
    } as Record<string, string>,
  },
} as const;

export function ROICalculator() {
  const { t, isRTL, locale } = useTranslation();
  const c = locale === 'ar' ? CONTENT.ar : CONTENT.en;
  const [step, setStep] = useState<Step>(1);
  const [ticketingSystem, setTicketingSystem] = useState<string | null>(null);
  const [channels, setChannels] = useState<string[]>([]);
  const [interactionType, setInteractionType] = useState<
    'inbound' | 'outbound' | 'both' | null
  >(null);
  // The calculator computes in whatever currency it displays, so the rate and
  // the salary bounds move together. SAR figures are the USD ones at the 3.75
  // peg, rounded to a sensible slider step. The riyal rate is a whole 3, not
  // 3.375, because the Arabic pages quote the price without decimals and the
  // arithmetic the reader sees has to match the rate printed beside it.
  const CURRENCY = isRTL
    ? {
        ratePerResolution: 3,
        rateLabel: '٣ ⃁',
        salaryDefault: 243750,
        salaryMin: 112500,
        salaryMax: 562500,
        salaryStep: 18750,
        salaryFloor: 37500,
      }
    : {
        // Top of the published $0.75-$0.90 range on purpose: the calculator
        // should under-promise, and a lower rate would inflate every saving on
        // screen. The footnote says so rather than presenting it as the price.
        ratePerResolution: 0.9,
        rateLabel: '$0.90',
        salaryDefault: 65000,
        salaryMin: 30000,
        salaryMax: 150000,
        salaryStep: 5000,
        salaryFloor: 10000,
      };

  // Arabic-Indic digits for the Arabic locale, untouched otherwise.
  const num = (v: string | number) =>
    isRTL
      ? String(v)
          .replace(/[0-9]/g, (d) => '٠١٢٣٤٥٦٧٨٩'[Number(d)])
          // A no-break space groups thousands, so the comma is free to serve
          // as the decimal mark (Thmanyah has no U+066B glyph).
          .replace(/(?<=[٠-٩]),(?=[٠-٩])/g, '\u00A0')
          .replace(/(?<=[٠-٩])\.(?=[٠-٩])/g, ',')
      : String(v);

  const money = (value: number) => {
    const n = num(Math.round(value).toLocaleString('en-US'));
    return isRTL ? `${n} ⃁` : `$${n}`;
  };

  const [monthlyVolume, setMonthlyVolume] = useState(10000);
  const [supportEmployees, setSupportEmployees] = useState(5);
  const [annualCostPerEmployee, setAnnualCostPerEmployee] = useState(CURRENCY.salaryDefault);

  // Ticketing systems with their logos
  const TICKETING_SYSTEMS = [
    { id: 'open', name: t.helpdesks['Open'], logo: '/open-logo.svg' },
    {
      id: 'zendesk',
      name: t.helpdesks['Zendesk'],
      logo: '/helpdicks/zendesk.png',
    },
    {
      id: 'freshdesk',
      name: t.helpdesks['Freshdesk'],
      logo: '/helpdicks/freshdesk.png',
    },
    {
      id: 'intercom',
      name: t.helpdesks['Intercom'],
      logo: '/helpdicks/intercom.png',
    },
    {
      id: 'hubspot',
      name: t.helpdesks['HubSpot'],
      logo: '/helpdicks/hubspot.png',
    },
    {
      id: 'salesforce',
      name: t.helpdesks['Salesforce'],
      logo: '/helpdicks/salesforce.png',
    },
    { id: 'front', name: 'Front', logo: '/helpdicks/front.png' },
    {
      id: 'flex',
      name: t.helpdesks['Twilio Flex'],
      logo: '/helpdicks/flex.png',
    },
    { id: 'other', name: t.demo['Other'], logo: null },
  ];

  const CHANNELS = [
    {
      id: 'email',
      name: t.channels['Email'],
      icon: Mail,
      automationRate: 0.8,
    },
    {
      id: 'phone',
      name: t.channels['Voice AI'],
      icon: Phone,
      automationRate: 0.6,
    },
    { id: 'sms', name: 'SMS', icon: Smartphone, automationRate: 0.75 },
    {
      id: 'whatsapp',
      name: t.channels['WhatsApp'],
      icon: MessageCircle,
      automationRate: 0.8,
    },
    {
      id: 'social',
      name: t.channels['Social'],
      icon: Share2,
      automationRate: 0.7,
    },
    {
      id: 'webchat',
      name: t.channels['Web Chat'],
      icon: MessageSquare,
      automationRate: 0.85,
    },
    {
      id: 'appchat',
      name: t.tools_pages['In-App Chat'],
      icon: MessageSquare,
      automationRate: 0.85,
    },
  ];

  const toggleChannel = (id: string) => {
    setChannels((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
    );
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return ticketingSystem !== null;
      case 2:
        return channels.length > 0;
      case 3:
        return interactionType !== null;
      case 4:
        return monthlyVolume >= 100;
      case 5:
        return supportEmployees >= 1 && annualCostPerEmployee >= CURRENCY.salaryFloor;
      default:
        return false;
    }
  };

  const nextStep = () => {
    if (step === 5) {
      setStep('email');
    } else if (typeof step === 'number') {
      setStep((step + 1) as Step);
    }
  };

  const prevStep = () => {
    if (step === 'result') {
      setStep('email');
    } else if (step === 'email') {
      setStep(5);
    } else if (typeof step === 'number' && step > 1) {
      setStep((step - 1) as Step);
    }
  };

  // Calculate ROI with detailed breakdown
  const calculateROI = () => {
    // Calculate weighted automation rate based on selected channels
    const selectedChannels = CHANNELS.filter((c) => channels.includes(c.id));
    const channelRates = selectedChannels.map((c) => ({
      name: c.name,
      rate: c.automationRate,
    }));
    const avgAutomationRate =
      selectedChannels.reduce((sum, c) => sum + c.automationRate, 0) /
      selectedChannels.length;

    // Outbound typically has lower automation rate
    const typeMultiplier =
      interactionType === 'outbound'
        ? 0.85
        : interactionType === 'both'
          ? 0.9
          : 1;
    const finalAutomationRate = avgAutomationRate * typeMultiplier;

    // Current state
    const yearlyTickets = monthlyVolume * 12;
    const currentYearlyCost = supportEmployees * annualCostPerEmployee;
    const currentMonthlyCost = currentYearlyCost / 12;

    // Cost per ticket with humans
    const costPerTicketHuman = currentYearlyCost / yearlyTickets;

    // With Open
    const automatedTicketsMonthly = Math.floor(
      monthlyVolume * finalAutomationRate,
    );
    const humanTicketsMonthly = monthlyVolume - automatedTicketsMonthly;
    const automatedTicketsYearly = automatedTicketsMonthly * 12;

    // Open pricing: simple flat rate for this calculator
    // (In reality, enterprise customers negotiate better rates)
    const OPEN_RATE_PER_RESOLUTION = CURRENCY.ratePerResolution;
    const openCostAutomatedMonthly =
      automatedTicketsMonthly * OPEN_RATE_PER_RESOLUTION;
    const openCostAutomatedYearly = openCostAutomatedMonthly * 12;

    // Staff savings calculation
    // For small teams: calculate based on capacity freed up
    // For larger teams: direct headcount reduction
    let staffReduction: number;
    let remainingStaffNeeded: number;

    if (supportEmployees <= 3) {
      // Small teams: proportional cost savings (keep at least 1 person)
      // Automation frees up capacity = saves proportional salary cost
      const capacityFreedUp = finalAutomationRate;
      staffReduction = supportEmployees * capacityFreedUp; // Can be fractional for small teams
      remainingStaffNeeded = Math.max(0.5, supportEmployees - staffReduction); // Allow fractional for calculation
    } else {
      // Larger teams: direct headcount reduction
      const staffReductionRate = finalAutomationRate * 0.85; // 85% efficiency in translating automation to staff savings
      staffReduction = Math.floor(supportEmployees * staffReductionRate);
      remainingStaffNeeded = Math.max(1, supportEmployees - staffReduction);
    }

    const openCostHumanYearly = remainingStaffNeeded * annualCostPerEmployee;
    const openCostHumanMonthly = openCostHumanYearly / 12;

    const openMonthlyCost = openCostAutomatedMonthly + openCostHumanMonthly;
    const openYearlyCost = openCostAutomatedYearly + openCostHumanYearly;

    // Savings
    const monthlySavings = currentMonthlyCost - openMonthlyCost;
    const yearlySavings = currentYearlyCost - openYearlyCost;
    const savingsPercentage = (yearlySavings / currentYearlyCost) * 100;

    return {
      // Summary
      currentYearlyCost,
      openYearlyCost,
      yearlySavings,
      savingsPercentage,
      automationRate: finalAutomationRate * 100,
      automatedTickets: automatedTicketsYearly,
      // Detailed breakdown
      breakdown: {
        monthlyVolume,
        supportEmployees,
        annualCostPerEmployee,
        channelRates,
        avgChannelRate: avgAutomationRate * 100,
        typeMultiplier,
        interactionType: interactionType || 'inbound',
        finalAutomationRate: finalAutomationRate * 100,
        automatedTicketsMonthly,
        humanTicketsMonthly,
        staffReduction: Math.floor(staffReduction), // Round for display
        remainingStaffNeeded: Math.ceil(remainingStaffNeeded), // Round up for display
        currentMonthlyCost,
        openCostAutomated: openCostAutomatedMonthly,
        openCostHuman: openCostHumanMonthly,
        openMonthlyCost,
        monthlySavings,
        costPerTicketHuman,
      },
    };
  };

  const results = step === 'result' ? calculateROI() : null;

  const formatCurrency = (value: number) => {
    if (value >= 1000000) {
      const m = num((value / 1000000).toFixed(2));
      return isRTL ? `${m} مليون ⃁` : `$${m}M`;
    }
    if (value >= 1000) {
      const k = num(Math.round(value / 1000));
      return isRTL ? `${k} ألف ⃁` : `$${k}K`;
    }
    return money(value);
  };

  return (
    <section className="min-h-screen bg-white pt-32 pb-20 md:pt-40">
      <div className="container max-w-3xl">
        {/* Progress */}
        {step !== 'result' && step !== 'email' && (
          <div className="mb-12">
            <div className="mb-2 flex justify-between text-sm text-neutral-400">
              <span>
                {t.tools_pages['Step']}{' '}
                {locale === 'ar' ? toArabicDigits(step as number) : step}{' '}
                {t.tools_pages['of 5']}
              </span>
            </div>
            <div className="h-1 overflow-hidden rounded-full bg-neutral-100">
              <motion.div
                className="h-full bg-black"
                initial={{ width: 0 }}
                animate={{ width: `${(step as number) * 20}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>
        )}

        <AnimatePresence mode="wait">
          {/* Step 1: Ticketing System */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <div>
                <h1 className="mb-2 text-3xl md:text-4xl text-black">
                  {t.tools_pages['What ticketing system do you use?']}
                </h1>
                <p className="text-neutral-500">
                  {
                    t.tools_pages[
                      'Select your current helpdesk or ticketing platform.'
                    ]
                  }
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                {TICKETING_SYSTEMS.map((system) => (
                  <button
                    key={system.id}
                    onClick={() => setTicketingSystem(system.id)}
                    className={`relative flex flex-col items-center gap-3 rounded-2xl border-2 p-6 transition-all ${
                      ticketingSystem === system.id
                        ? 'border-black bg-neutral-50'
                        : 'border-neutral-200 hover:border-neutral-300'
                    }`}
                  >
                    {ticketingSystem === system.id && (
                      <div className="absolute top-3 right-3 flex size-5 items-center justify-center rounded-full bg-black">
                        <Check className="size-3 text-white" />
                      </div>
                    )}
                    {system.logo ? (
                      <div className="relative size-12 overflow-hidden rounded-xl">
                        <Image
                          src={system.logo}
                          alt={system.name}
                          fill
                          className="object-contain"
                        />
                      </div>
                    ) : (
                      <div className="flex size-12 items-center justify-center rounded-xl bg-neutral-100 text-xl">
                        ?
                      </div>
                    )}
                    <span className="text-sm font-medium">{system.name}</span>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 2: Channels */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <div>
                <h1 className="mb-2 text-3xl md:text-4xl text-black">
                  {t.tools_pages['What channels do you want to automate?']}
                </h1>
                <p className="text-neutral-500">
                  {t.tools_pages['Select all that apply.']}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                {CHANNELS.map((channel) => {
                  const Icon = channel.icon;
                  const isSelected = channels.includes(channel.id);
                  return (
                    <button
                      key={channel.id}
                      onClick={() => toggleChannel(channel.id)}
                      className={`flex items-center gap-3 rounded-2xl border-2 p-5 transition-all ${
                        isSelected
                          ? 'border-black bg-neutral-50'
                          : 'border-neutral-200 hover:border-neutral-300'
                      }`}
                    >
                      <div
                        className={`flex size-10 items-center justify-center rounded-xl ${
                          isSelected ? 'bg-black text-white' : 'bg-neutral-100'
                        }`}
                      >
                        <Icon className="size-5" />
                      </div>
                      <span className="font-medium text-black">
                        {channel.name}
                      </span>
                      {isSelected && (
                        <Check
                          className={`${isRTL ? 'mr-auto' : 'ml-auto'} size-5 text-black`}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Step 3: Interaction Type */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <div>
                <h1 className="mb-2 text-3xl md:text-4xl text-black">
                  {t.tools_pages['What type of interactions?']}
                </h1>
                <p className="text-neutral-500">
                  {
                    t.tools_pages[
                      'Are these customer-initiated or company-initiated?'
                    ]
                  }
                </p>
              </div>

              <div className="grid gap-4">
                {[
                  {
                    id: 'inbound',
                    title: c.interactionTypes.inbound,
                    desc: t.tools_pages['Customers reaching out to you'],
                  },
                  {
                    id: 'outbound',
                    title: c.interactionTypes.outbound,
                    desc: t.tools_pages['You reaching out to customers'],
                  },
                  {
                    id: 'both',
                    title: c.interactionTypes.both,
                    desc: t.tools_pages['A mix of inbound and outbound'],
                  },
                ].map((type) => (
                  <button
                    key={type.id}
                    onClick={() =>
                      setInteractionType(
                        type.id as 'inbound' | 'outbound' | 'both',
                      )
                    }
                    className={`flex items-center justify-between rounded-2xl border-2 p-6 transition-all ${
                      interactionType === type.id
                        ? 'border-black bg-neutral-50'
                        : 'border-neutral-200 hover:border-neutral-300'
                    } ${isRTL ? 'text-right' : 'text-left'}`}
                  >
                    <div>
                      <div className="font-semibold text-black">
                        {type.title}
                      </div>
                      <div className="text-sm text-neutral-500">
                        {type.desc}
                      </div>
                    </div>
                    {interactionType === type.id && (
                      <Check className="size-6 text-black" />
                    )}
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 4: Volume */}
          {step === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <div>
                <h1 className="mb-2 text-3xl md:text-4xl text-black">
                  {t.tools_pages['How many interactions per month?']}
                </h1>
                <p className="text-neutral-500">
                  {
                    t.tools_pages[
                      'Total tickets, conversations, or calls across all channels.'
                    ]
                  }
                </p>
              </div>

              <div className="space-y-6 rounded-2xl border border-neutral-200 p-8">
                <div className="text-center">
                  <div className="text-6xl font-bold tabular-nums text-black">
                    {monthlyVolume.toLocaleString()}
                  </div>
                  <div className="text-neutral-500">
                    {t.tools_pages['interactions per month']}
                  </div>
                </div>

                <input
                  type="range"
                  min="1000"
                  max="500000"
                  step="1000"
                  value={monthlyVolume}
                  onChange={(e) => setMonthlyVolume(Number(e.target.value))}
                  className="h-3 w-full cursor-pointer appearance-none rounded-full bg-neutral-100 accent-black"
                />

                <div className="flex justify-between text-sm text-neutral-400">
                  <span>1K</span>
                  <span>500K</span>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 5: Team Size & Cost */}
          {step === 5 && (
            <motion.div
              key="step5"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <div>
                <h1 className="mb-2 text-3xl md:text-4xl text-black">
                  {t.tools_pages['Tell us about your support team']}
                </h1>
                <p className="text-neutral-500">
                  {
                    t.tools_pages[
                      'This helps us calculate your potential savings.'
                    ]
                  }
                </p>
              </div>

              {/* Number of Employees */}
              <div className="space-y-6 rounded-2xl border border-neutral-200 p-8">
                <div>
                  <label className="mb-4 block text-sm font-medium text-neutral-700">
                    {t.tools_pages['Number of support employees']}
                  </label>
                  <div className="text-center">
                    <div className="text-6xl font-bold tabular-nums text-black">
                      {supportEmployees}
                    </div>
                    <div className="text-neutral-500">
                      {t.tools_pages['employees']}
                    </div>
                  </div>

                  <input
                    type="range"
                    min="1"
                    max="100"
                    step="1"
                    value={supportEmployees}
                    onChange={(e) =>
                      setSupportEmployees(Number(e.target.value))
                    }
                    className="mt-6 h-3 w-full cursor-pointer appearance-none rounded-full bg-neutral-100 accent-black"
                  />

                  <div className="mt-2 flex justify-between text-sm text-neutral-400">
                    <span>1</span>
                    <span>100</span>
                  </div>
                </div>
              </div>

              {/* Annual Cost Per Employee */}
              <div className="space-y-6 rounded-2xl border border-neutral-200 p-8">
                <div>
                  <label className="mb-4 block text-sm font-medium text-neutral-700">
                    {t.tools_pages['Annual cost per employee']}
                  </label>
                  <div className="text-center">
                    <div className="text-6xl font-bold tabular-nums text-black">
                      {money(annualCostPerEmployee)}
                    </div>
                    <div className="text-neutral-500">
                      {t.tools_pages['per year']}
                    </div>
                  </div>

                  <input
                    type="range"
                    min={CURRENCY.salaryMin}
                    max={CURRENCY.salaryMax}
                    step={CURRENCY.salaryStep}
                    value={annualCostPerEmployee}
                    onChange={(e) =>
                      setAnnualCostPerEmployee(Number(e.target.value))
                    }
                    className="mt-6 h-3 w-full cursor-pointer appearance-none rounded-full bg-neutral-100 accent-black"
                  />

                  <div className="mt-2 flex justify-between text-sm text-neutral-400">
                    <span>{formatCurrency(CURRENCY.salaryMin)}</span>
                    <span>{formatCurrency(CURRENCY.salaryMax)}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Email Gate */}
          {step === 'email' && (
            <EmailGateStep
              key="email"
              tool="ROI Calculator"
              onSubmit={() => setStep('result')}
              onBack={prevStep}
            />
          )}

          {/* Result */}
          {step === 'result' && results && (
            <motion.div
              key="result"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="space-y-8"
            >
              {results.yearlySavings > 0 ? (
                <>
                  <div className="text-center">
                    <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-green-100 px-4 py-2 text-sm font-medium text-green-700">
                      <Sparkles className="size-4" />
                      {t.tools_pages['Your personalized savings']}
                    </div>
                    <h1 className="text-4xl md:text-5xl text-black">
                      {t.tools_pages['You could save']}
                    </h1>
                  </div>

                  {/* Main savings highlight */}
                  <div className="rounded-3xl bg-black p-10 text-center text-white">
                    <div className="mb-2 text-6xl font-bold tracking-tight md:text-7xl">
                      {formatCurrency(results.yearlySavings)}
                    </div>
                    <div className="text-lg text-neutral-400">
                      {t.tools_pages['per year']}
                    </div>
                    <div className="mt-4 text-sm text-neutral-500">
                      {c.thats} {formatCurrency(results.yearlySavings / 12)}/
                      {t.footer['month'] || 'month'}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-center">
                    <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-blue-100 px-4 py-2 text-sm font-medium text-blue-700">
                      <Check className="size-4" />
                      {t.tools_pages['Your results']}
                    </div>
                    <h1 className="text-4xl md:text-5xl text-black">
                      {t.tools_pages['You are already doing great!']}
                    </h1>
                  </div>

                  {/* Main message */}
                  <div className="rounded-3xl bg-gradient-to-br from-blue-50 to-indigo-50 p-10 text-center">
                    <div className="mb-4 text-2xl font-semibold text-neutral-900">
                      {
                        t.tools_pages[
                          'Open might not be a fit for you right now.'
                        ]
                      }
                    </div>
                    <p className="mx-auto max-w-xl text-base text-neutral-600">
                      {
                        t.tools_pages[
                          'Your current setup is already cost-efficient! We recommend sticking with what works. Come back when you scale and need to handle more volume.'
                        ]
                      }
                    </p>
                  </div>
                </>
              )}

              {/* Simple comparison table - only show if positive savings */}
              {results.yearlySavings > 0 && (
                <div className="overflow-hidden rounded-2xl border border-neutral-200">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-neutral-200 bg-neutral-50">
                        <th
                          className={`px-6 py-4 ${isRTL ? 'text-right' : 'text-left'} text-sm font-medium text-neutral-500`}
                        >
                          {t.helpdesk['Metric']}
                        </th>
                        <th
                          className={`px-6 py-4 ${isRTL ? 'text-left' : 'text-right'} text-sm font-medium text-neutral-500`}
                        >
                          {t.tools_pages['Today']}
                        </th>
                        <th
                          className={`px-6 py-4 ${isRTL ? 'text-left' : 'text-right'} text-sm font-medium text-neutral-500`}
                        >
                          {t.tools_pages['With Open']}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                      <tr>
                        <td className="px-6 py-4 text-sm text-neutral-600">
                          {t.tools_pages['Annual cost']}
                        </td>
                        <td
                          className={`px-6 py-4 ${isRTL ? 'text-left' : 'text-right'} text-sm font-medium text-neutral-400 line-through`}
                        >
                          {formatCurrency(results.currentYearlyCost)}
                        </td>
                        <td
                          className={`px-6 py-4 ${isRTL ? 'text-left' : 'text-right'} text-sm font-semibold text-green-600`}
                        >
                          {formatCurrency(results.openYearlyCost)}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 text-sm text-neutral-600">
                          {t.tools_pages['Support team size']}
                        </td>
                        <td
                          className={`px-6 py-4 ${isRTL ? 'text-left' : 'text-right'} text-sm font-medium text-neutral-400`}
                        >
                          {results.breakdown.supportEmployees}{' '}
                          {t.tools_pages['employees']}
                        </td>
                        <td
                          className={`px-6 py-4 ${isRTL ? 'text-left' : 'text-right'} text-sm font-semibold text-green-600`}
                        >
                          {results.breakdown.remainingStaffNeeded}{' '}
                          {t.tools_pages['employees']}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 text-sm text-neutral-600">
                          {t.tools_pages['Automation rate']}
                        </td>
                        <td
                          className={`px-6 py-4 ${isRTL ? 'text-left' : 'text-right'} text-sm font-medium text-neutral-400`}
                        >
                          0%
                        </td>
                        <td
                          className={`px-6 py-4 ${isRTL ? 'text-left' : 'text-right'} text-sm font-semibold text-green-600`}
                        >
                          {results.automationRate.toFixed(0)}%
                        </td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 text-sm text-neutral-600">
                          {t.tools_pages['Tickets automated/year']}
                        </td>
                        <td
                          className={`px-6 py-4 ${isRTL ? 'text-left' : 'text-right'} text-sm font-medium text-neutral-400`}
                        >
                          0
                        </td>
                        <td
                          className={`px-6 py-4 ${isRTL ? 'text-left' : 'text-right'} text-sm font-semibold text-green-600`}
                        >
                          {results.automatedTickets.toLocaleString()}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {/* Upsell to Open helpdesk if not already selected */}
              {results.yearlySavings > 0 && ticketingSystem !== 'open' && (
                <div className="rounded-2xl border-2 border-dashed border-blue-200 bg-blue-50 p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-blue-100">
                      <Sparkles className="size-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-neutral-900">
                        {t.tools_pages['Save even more with Open Helpdesk']}
                      </h3>
                      <p className="mt-1 text-sm text-neutral-600">
                        {c.currentlyUsing}{' '}
                        {TICKETING_SYSTEMS.find((s) => s.id === ticketingSystem)
                          ?.name || c.anotherHelpdesk}
                        {'. '}
                        {c.switchToOpen}{' '}
                        <span className="font-semibold">
                          {t.tools_pages['pay per resolution, not per agent']}
                        </span>
                        . {c.noHiddenCosts}
                      </p>
                      <Link
                        href="/helpdesks/open"
                        prefetch={true}
                        className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700"
                      >
                        {t.nav['Learn more'] || 'Learn more'}
                        <ArrowRight
                          className={`size-4 ${isRTL ? 'rotate-180' : ''}`}
                        />
                      </Link>
                    </div>
                  </div>
                </div>
              )}

              {/* Learn More Modal - only show if positive savings */}
              {results.yearlySavings > 0 && (
                <Dialog>
                  <DialogTrigger asChild>
                    <button className="flex w-full items-center justify-center gap-2 rounded-2xl border border-neutral-200 py-4 text-sm font-medium text-neutral-600 transition-all hover:bg-neutral-50">
                      <Calculator className="size-4" />
                      {
                        t.tools_pages[
                          'See the exact math behind this calculation'
                        ]
                      }
                    </button>
                  </DialogTrigger>
                  <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2 text-xl">
                        <Calculator className="size-5" />
                        {t.tools_pages['Calculation Breakdown']}
                      </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-6 pt-4">
                      {/* Step 1: Your Inputs */}
                      <div className="rounded-xl bg-neutral-50 p-4">
                        <h3 className="mb-3 text-neutral-900">
                          {t.tools_pages['Step 1: Your Inputs']}
                        </h3>
                        <div className="space-y-2 font-mono text-sm">
                          <div className="flex justify-between">
                            <span className="text-neutral-600">
                              {t.tools_pages['interactions per month']}
                            </span>
                            <span className="font-semibold">
                              {results.breakdown.monthlyVolume.toLocaleString()}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-neutral-600">
                              {t.tools_pages['Number of support employees']}
                            </span>
                            <span className="font-semibold">
                              {results.breakdown.supportEmployees}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-neutral-600">
                              {t.tools_pages['Annual cost per employee']}
                            </span>
                            <span className="font-semibold">
                              {money(results.breakdown.annualCostPerEmployee)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-neutral-600">
                              {t.tools_pages['What type of interactions?']}
                            </span>
                            <span className="font-semibold capitalize">
                              {c.interactionTypes[
                                results.breakdown.interactionType
                              ] || results.breakdown.interactionType}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Step 2: Automation Rate */}
                      <div className="rounded-xl bg-neutral-50 p-4">
                        <h3 className="mb-3 text-neutral-900">
                          {t.tools_pages['Step 2: Automation Rate Calculation']}
                        </h3>
                        <div className="space-y-2 font-mono text-sm">
                          <div className="text-neutral-600">
                            {c.channelRates}
                          </div>
                          {results.breakdown.channelRates.map(
                            (ch: { name: string; rate: number }, i: number) => (
                              <div
                                key={i}
                                className={`flex justify-between ${isRTL ? 'pr-4' : 'pl-4'} text-neutral-500`}
                              >
                                <span>• {ch.name}</span>
                                <span>{(ch.rate * 100).toFixed(0)}%</span>
                              </div>
                            ),
                          )}
                          <div className="flex justify-between border-t border-neutral-200 pt-2">
                            <span className="text-neutral-600">
                              {c.avgChannelRate}
                            </span>
                            <span className="font-semibold">
                              {results.breakdown.avgChannelRate.toFixed(1)}%
                            </span>
                          </div>
                          {results.breakdown.typeMultiplier < 1 && (
                            <div className="flex justify-between">
                              <span className="text-neutral-600">
                                {c.interactionTypes[
                                  results.breakdown.interactionType
                                ] || results.breakdown.interactionType}{' '}
                                {c.multiplier}
                              </span>
                              <span className="font-semibold">
                                × {results.breakdown.typeMultiplier}
                              </span>
                            </div>
                          )}
                          <div className="flex justify-between border-t border-neutral-200 pt-2">
                            <span className="text-neutral-600">
                              {t.tools_pages['Automation rate']}
                            </span>
                            <span className="font-bold text-black">
                              {results.breakdown.finalAutomationRate.toFixed(1)}
                              %
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Step 3: Current Cost */}
                      <div className="rounded-xl bg-neutral-50 p-4">
                        <h3 className="mb-3 text-neutral-900">
                          {t.tools_pages['Step 3: Your Current Monthly Cost']}
                        </h3>
                        <div className="space-y-2 font-mono text-sm">
                          <div className="text-neutral-500">
                            {results.breakdown.supportEmployees}{' '}
                            {t.tools_pages['employees']} ×{' '}
                            {money(results.breakdown.annualCostPerEmployee)}
                            /{t.tools_pages['per year']} ÷ 12
                          </div>
                          <div className="flex justify-between text-lg">
                            <span className="text-neutral-600">=</span>
                            <span className="font-bold">
                              {money(results.breakdown.currentMonthlyCost)}
                              /{t.footer['month'] || 'month'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Step 4: Cost with Open */}
                      <div className="rounded-xl bg-neutral-50 p-4">
                        <h3 className="mb-3 text-neutral-900">
                          {t.tools_pages['Step 4: Cost with Open']}
                        </h3>
                        <div className="space-y-2 font-mono text-sm">
                          <div className="flex justify-between">
                            <span className="text-neutral-600">
                              {t.tools_pages['Automated tickets']} (
                              {results.breakdown.finalAutomationRate.toFixed(0)}
                              %)
                            </span>
                            <span>
                              {results.breakdown.automatedTicketsMonthly.toLocaleString()}
                            </span>
                          </div>
                          <div
                            className={`flex justify-between ${isRTL ? 'pr-4' : 'pl-4'} text-neutral-500`}
                          >
                            <span>
                              {results.breakdown.automatedTicketsMonthly.toLocaleString()}{' '}
                              × {CURRENCY.rateLabel}
                            </span>
                            <span>
                              = {money(results.breakdown.openCostAutomated)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-neutral-600">
                              {t.tools_pages['Remaining staff needed']}
                            </span>
                            <span>
                              {results.breakdown.remainingStaffNeeded}{' '}
                              {t.tools_pages['employees']}
                            </span>
                          </div>
                          <div
                            className={`flex justify-between ${isRTL ? 'pr-4' : 'pl-4'} text-neutral-500`}
                          >
                            <span>
                              {results.breakdown.remainingStaffNeeded} ×{' '}
                              {money(results.breakdown.annualCostPerEmployee)} ÷ 12
                            </span>
                            <span>
                              = {money(results.breakdown.openCostHuman)}
                            </span>
                          </div>
                          <div className="flex justify-between border-t border-neutral-200 pt-2 text-lg">
                            <span className="text-neutral-600">
                              {t.tools_pages['Total with Open']}
                            </span>
                            <span className="font-bold text-green-600">
                              {money(results.breakdown.openMonthlyCost)}
                              /{t.footer['month'] || 'month'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Step 5: Savings */}
                      <div className="rounded-xl bg-black p-4 text-white">
                        <h3 className="mb-3">
                          {t.tools_pages['Step 5: Your Savings']}
                        </h3>
                        <div className="space-y-2 font-mono text-sm">
                          <div className="flex justify-between text-neutral-300">
                            <span>{t.tools_pages['Monthly savings']}</span>
                            <span>
                              {money(results.breakdown.currentMonthlyCost)}{' '}
                              - {money(results.breakdown.openMonthlyCost)} ={' '}
                              <span className="text-white">
                                {money(results.breakdown.monthlySavings)}
                              </span>
                            </span>
                          </div>
                          <div className="flex justify-between border-t border-white/20 pt-2 text-lg">
                            <span>{t.tools_pages['Annual savings']}</span>
                            <span className="font-bold">
                              {money(results.breakdown.monthlySavings)} × 12 ={' '}
                              {money(results.yearlySavings)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Sources */}
                      <div className="text-xs text-neutral-400">
                        <p>
                          {
                            t.tools_pages[
                              'Automation rates based on 2024 industry data. Priced at $0.90 per resolution — the top of the published $0.75-$0.90 range, so the saving shown is the conservative one. Calculation assumes proportional staff reduction with automation.'
                            ]
                          }
                        </p>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              )}

              {results.yearlySavings > 0 && (
                <Link
                  href="/demo"
                  prefetch={true}
                  aria-label={t.common['Learn more about Open and book a demo']}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-black py-4 text-lg font-semibold text-white transition-all hover:bg-neutral-800"
                >
                  {t.tools_pages['Get started now']}
                  <ArrowRight
                    className={`size-5 ${isRTL ? 'rotate-180' : ''}`}
                  />
                </Link>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation */}
        {step !== 'result' && step !== 'email' && (
          <div className="mt-12 flex items-center justify-between">
            <button
              onClick={prevStep}
              disabled={step === 1}
              className={`flex items-center gap-2 rounded-full px-6 py-3 font-medium transition-all ${
                step === 1
                  ? 'cursor-not-allowed text-neutral-300'
                  : 'text-neutral-600 hover:bg-neutral-100'
              }`}
            >
              <ArrowLeft className={`size-5 ${isRTL ? 'rotate-180' : ''}`} />
              {t.tools_pages['Back']}
            </button>

            <button
              onClick={nextStep}
              disabled={!canProceed()}
              className={`flex items-center gap-2 rounded-full px-8 py-3 font-semibold transition-all ${
                canProceed()
                  ? 'bg-black text-white hover:bg-neutral-800'
                  : 'cursor-not-allowed bg-neutral-200 text-neutral-400'
              }`}
            >
              {step === 5
                ? t.tools_pages['See my savings']
                : t.tools_pages['Continue']}
              <ArrowRight className={`size-5 ${isRTL ? 'rotate-180' : ''}`} />
            </button>
          </div>
        )}

        {step === 'result' && (
          <div className="mt-8 text-center">
            <button
              onClick={() => setStep(1)}
              className="text-neutral-500 underline hover:text-neutral-700"
            >
              {t.tools_pages['Start over']}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
